import api from '../api/axios';

const extractFilename = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;

  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1]);
  }

  const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  return fallback;
};
const extractErrorMessage = async (error, fallbackMessage) => {
  const payload = error?.response?.data;
  
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (payload instanceof Blob) {
    const text = await payload.text().catch(() => '');
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (typeof parsed?.message === 'string' && parsed.message.trim()) {
          return parsed.message;
        }
      } catch {
        // ignore JSON parse failure and fall back to plain text handling
      }

      const normalized = text.trim();
      if (normalized && normalized.length <= 240) {
        return normalized;
      }
    }
  }

  if (typeof error?.response?.data?.message === 'string' && error.response.data.message.trim()) {
    return error.response.data.message;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

const shouldRetryPdfDownload = (error) => {
  const status = error?.response?.status;
  const isTimeout = error?.code === 'ECONNABORTED' || String(error?.message || '').includes('timeout');

  if (isTimeout) return true;
  if (typeof status === 'number' && status >= 500) return true;
  return false;
};

export async function downloadInspectionReportPdf(inspectionId, options = {}) {
  const fallbackMessage = 'تعذر تنزيل التقرير PDF الآن. حاول مرة أخرى.';
  const theme = String(options?.theme || '').trim();

  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const response = await api.get(`/inspections/${inspectionId}/report/pdf`, {
        responseType: 'blob',
        params: theme ? { theme } : undefined,
        timeout: attempt === 1 ? 90000 : 180000,
      });

      const contentType = (response.headers['content-type'] || 'application/pdf').toLowerCase();

      if (!contentType.includes('application/pdf')) {
        const serverMessage = await extractErrorMessage({ response }, fallbackMessage);
        throw new Error(serverMessage || fallbackMessage);
      }

      const filename = extractFilename(
        response.headers['content-disposition'],
        `inspection-report-${inspectionId}.pdf`
      );

      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // لا تُلغِ الرابط مباشرة بعد click لأن بعض المتصفحات قد تنتج ملفًا تالفًا
      // إذا تم revoke مبكرًا أثناء بدء التحميل.
      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60_000);
      return;
    } catch (error) {
      if (attempt < 2 && shouldRetryPdfDownload(error)) {
        continue;
      }

      const message = await extractErrorMessage(error, fallbackMessage);
      throw new Error(message || fallbackMessage);
    }
  }
}

export async function createInspectionReportShareLink(inspectionId, options = {}) {
  const fallbackMessage = 'تعذر إنشاء رابط مشاركة التقرير الآن. حاول مرة أخرى.';
  const theme = String(options?.theme || '').trim();

  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const response = await api.post(
        `/inspections/${inspectionId}/report/pdf/share`,
        {},
        {
          params: theme ? { theme } : undefined,
          timeout: attempt === 1 ? 90000 : 180000,
        }
      );

      const url = String(response?.data?.url || '').trim();
      if (!url) {
        throw new Error('لم يتم استلام رابط صالح من الخادم.');
      }

      return {
        url,
        path: response?.data?.path || '',
        fileName: response?.data?.file_name || '',
      };
    } catch (error) {
      if (attempt < 2 && shouldRetryPdfDownload(error)) {
        continue;
      }

      const message = await extractErrorMessage(error, fallbackMessage);
      throw new Error(message || fallbackMessage);
    }
  }
}
